import React, { Component, useState } from 'react';
import { bool, func, object, shape, string, node } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { ValidationError, ExpandingTextarea, MultiPurposeInfo } from '../../components';

import css from './FieldTextInput.css';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    showWarning,
    warningMessage,
    icon,
    disabled,
    showSpacer = true,
    infoText,
    ...rest
  } = props;

  const [showPassword, setShowPassword] = useState(false);

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const isPassword = input.type === 'password';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  // Custom onBlur/onChange/onFocus are spread after {...input} below and
  // would replace Final Form's own handlers — without input.onBlur the field
  // never becomes touched and validation errors never render. Compose them
  // so both run.
  const {
    onBlur: customOnBlur,
    onChange: customOnChange,
    onFocus: customOnFocus,
    ...restProps
  } = rest;
  const composedHandlers = {};
  if (customOnBlur) {
    composedHandlers.onBlur = e => {
      input.onBlur(e);
      customOnBlur(e);
    };
  }
  if (customOnChange) {
    composedHandlers.onChange = e => {
      input.onChange(e);
      customOnChange(e);
    };
  }
  if (customOnFocus) {
    composedHandlers.onFocus = e => {
      input.onFocus(e);
      customOnFocus(e);
    };
  }

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
    });
  const maxLength = CONTENT_MAX_LENGTH;

  // For password fields with eye toggle, use text type when showing
  const effectiveType = isPassword && showPassword ? 'text' : type;

  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        disabled,
        ...refMaybe,
        ...inputWithoutType,
        ...restProps,
        ...composedHandlers,
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        type: effectiveType,
        defaultValue,
        disabled,
        ...refMaybe,
        ...inputWithoutValue,
        ...restProps,
        ...composedHandlers,
      }
    : {
        className: inputClasses,
        id,
        type: effectiveType,
        disabled,
        ...refMaybe,
        ...input,
        ...restProps,
        ...composedHandlers,
      };

  // Override type for password toggle
  if (!isTextarea && !isUncontrolled && isPassword) {
    inputProps.type = effectiveType;
  }

  const classes = classNames(rootClassName || css.root, className, {
    [css.disabled]: disabled,
  });

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      <div className={isPassword ? css.passwordWrapper : undefined}>
        {isTextarea ? <ExpandingTextarea {...inputProps} /> : <input {...inputProps} />}
        {isPassword ? (
          <button
            type="button"
            className={css.eyeToggle}
            onClick={() => setShowPassword(prev => !prev)}
            tabIndex={-1}
          >
            {showPassword ? (
              <AiOutlineEyeInvisible size={18} color="#717171" />
            ) : (
              <AiOutlineEye size={18} color="#717171" />
            )}
          </button>
        ) : null}
      </div>
      {infoText ? <div className={css.infoText}>{infoText}</div> : null}
      <ValidationError fieldMeta={fieldMeta} />
      <MultiPurposeInfo
        showWarning={showWarning}
        warningMessage={warningMessage}
        icon={icon}
        // useRed={true}
      />
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
  infoText: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,
  onUnmount: func,
  customErrorText: string,
  id: string,
  label: string,
  isUncontrolled: bool,
  inputRef: object,
  infoText: node,
  input: shape({
    onChange: func.isRequired,
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
